import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext.jsx";
import { useNotifications } from "../../context/NotificationContext.jsx";
import { useState } from "react";

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const { unreadCount }  = useNotifications();
  const [logoutState, setLogoutState] = useState("idle");

  const handleLogout = () => {
    if (logoutState === "idle") {
      setLogoutState("confirming");
      setTimeout(() => setLogoutState("idle"), 4000);
      return;
    }
    setLogoutState("loading");
    logout().then(() => navigate("/login")).catch(() => navigate("/login"));
  };

  const NAV = [
    { section: "Principal", items: [
      { to: "/dashboard",     icon: "bi-grid-1x2-fill",             label: "Dashboard" },
      { to: "/tickets",       icon: "bi-ticket-detailed-fill",      label: "Tickets",          badge: 0 },
      { to: "/assets",        icon: "bi-pc-display-horizontal",     label: "Parc informatique" },
      { to: "/inventory",     icon: "bi-clipboard2-check-fill",     label: "Inventaire" },
      { to: "/passwords",     icon: "bi-shield-lock-fill",          label: "Mots de passe" },
      { to: "/my-passwords",  icon: "bi-person-lock",                label: "Mes mots de passe" },
      { to: "/notifications", icon: "bi-bell-fill",                 label: "Notifications",    badge: unreadCount },
    ]},
    { section: "Gestion", items: [
      { to: "/clients",       icon: "bi-building",                  label: "Entreprises" },
      { to: "/users",         icon: "bi-people-fill",               label: "Utilisateurs" },
    ]},
    { section: "Système", items: [
      { to: "/sync",          icon: "bi-arrow-repeat",              label: "Sync Atera" },
      { to: "/settings",      icon: "bi-gear-fill",                 label: "Paramètres" },
      { to: "/profile",       icon: "bi-person-fill",               label: "Mon profil" },
    ]},
  ];

  return (
    <aside className="itm-sidebar">

      {/* ── Logo CPUc ── */}
      <div className="sidebar-logo">
        <img src="/logo-cpuc.png" alt="CPUc" className="sidebar-logo-img"/>
      </div>

      {/* ── Navigation ── */}
      <nav className="sidebar-nav">
        {NAV.map(group => (
          <div key={group.section}>
            <div className="nav-section-label">{group.section}</div>
            {group.items.map(item => (
              <NavLink key={item.to} to={item.to}
                className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}>
                <i className={`bi ${item.icon}`}/>
                <span>{item.label}</span>
                {item.badge > 0 && <span className="sidebar-badge">{item.badge}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* ── Footer ── */}
      <div className="sidebar-footer">
        <div className="user-pill" onClick={() => navigate("/profile")}>
          <div className="avatar-pill">{user?.firstName?.[0]}{user?.lastName?.[0]}</div>
          <div className="user-info">
            <div className="user-name">{user?.firstName} {user?.lastName}</div>
            <div className="user-role">{user?.role?.name ?? "Admin"}</div>
          </div>
        </div>

        <button
          className={`logout-btn ${logoutState === "confirming" ? "confirming" : ""}`}
          onClick={handleLogout}
          disabled={logoutState === "loading"}
        >
          {logoutState === "loading"    && <><i className="bi bi-arrow-repeat logout-spin"/><span>Déconnexion…</span></>}
          {logoutState === "confirming" && <><i className="bi bi-exclamation-triangle"/><span>Confirmer ?</span></>}
          {logoutState === "idle"       && <><i className="bi bi-box-arrow-right"/><span>Déconnexion</span></>}
        </button>
      </div>

      <style>{CSS}</style>
    </aside>
  );
}

const CSS = `
.itm-sidebar {
  width: var(--sidebar-w);
  background: #fff;
  border-right: 1px solid rgba(0,0,0,.08);
  display: flex; flex-direction: column;
  position: fixed; top: 0; left: 0; bottom: 0;
  z-index: 100; overflow: hidden;
  transition: width .3s cubic-bezier(.4,0,.2,1);
  box-shadow: 2px 0 12px rgba(0,0,0,.06);
}
.sidebar-logo {
  padding: 18px 16px;
  display: flex; align-items: center; gap: 10px;
  border-bottom: 1px solid rgba(0,0,0,.07); flex-shrink: 0;
}
.sidebar-logo-img { height: 30px; object-fit: contain; flex-shrink: 0; }
.sidebar-nav { flex: 1; padding: 8px 0; overflow-y: auto; overflow-x: hidden; }
.nav-section-label {
  font-size: 10px; font-weight: 600; letter-spacing: .12em;
  color: #9CA3AF; padding: 10px 16px 4px; text-transform: uppercase; white-space: nowrap;
}
.sidebar-item {
  display: flex; align-items: center; gap: 11px;
  padding: 9px 16px; cursor: pointer;
  border-left: 3px solid transparent;
  transition: all .18s; color: #4B5563;
  font-size: 13px; white-space: nowrap; text-decoration: none;
}
.sidebar-item i { font-size: 16px; flex-shrink: 0; }
.sidebar-item:hover { color: #1A1A1A; background: rgba(0,0,0,.04); }
.sidebar-item.active {
  color: #E63329; border-left-color: #E63329;
  background: rgba(230,51,41,.07); font-weight: 500;
}
.sidebar-item.active i { color: #E63329; }
.sidebar-badge {
  margin-left: auto; background: #E63329; color: #fff;
  border-radius: 20px; font-size: 10px; font-weight: 700;
  padding: 2px 7px; flex-shrink: 0;
}
.sidebar-footer { padding: 10px; border-top: 1px solid rgba(0,0,0,.07); flex-shrink: 0; }
.user-pill {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 10px; border-radius: 10px; cursor: pointer;
  transition: background .18s; overflow: hidden; margin-bottom: 6px;
}
.user-pill:hover { background: rgba(0,0,0,.04); }
.avatar-pill {
  width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(135deg,#E63329,#c92b22);
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; color: #fff; text-transform: uppercase;
}
.user-info { overflow: hidden; min-width: 0; }
.user-name { font-size: 13px; font-weight: 500; color: #1A1A1A; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.user-role { font-size: 11px; color: #9CA3AF; }
.logout-btn {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 10px; border-radius: 10px; width: 100%;
  background: none; border: none; cursor: pointer;
  color: #9CA3AF; font-size: 13px; transition: all .2s;
}
.logout-btn:not(.confirming):hover { color: #DC2626; background: rgba(220,38,38,.08); }
.logout-btn.confirming { color: #D97706; background: rgba(217,119,6,.08); border: 1px solid rgba(217,119,6,.2); }
.logout-btn i { font-size: 16px; flex-shrink: 0; }
@keyframes logout-spin { to { transform: rotate(360deg); } }
.logout-spin { animation: logout-spin .7s linear infinite; display: inline-block; }
@media (max-width: 1100px) {
  .sidebar-logo-text, .nav-section-label, .sidebar-item span,
  .sidebar-badge, .user-info, .logout-btn span { display: none !important; }
  .sidebar-item { justify-content: center; padding: 10px 0; border-left-width: 0; }
  .sidebar-logo { justify-content: center; padding: 16px 8px; }
  .user-pill, .logout-btn { justify-content: center; }
}
@media (max-width: 768px) { .itm-sidebar { display: none; } }
`;